import { PermissionGate } from "@/components/common/PermissionGate"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { PERMISSIONS } from "@/constants/permissions"
import { useDebouncedValue } from "@/hooks/useDebouncedValue"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { useQueryClient } from "@tanstack/react-query"
import {
  Check,
  ChevronDown,
  Info,
  Loader2,
  Plus,
  TriangleAlert,
  X,
} from "lucide-react"
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"
import {
  refreshExemptionsAfter,
  useDeclarationExemptions,
} from "../../../../../api/declarationsApi"
import {
  useGlobalSubstanceDetail,
  useGlobalSubstanceList,
} from "../../../../../api/substancesApi"
import type { ConditionFieldRow } from "../../../../../api/types"
import type { SubstanceOut } from "../../../../../api/types/masterData"
import { useReviewPaged } from "../../../../../hooks/useReviewPaged"
import {
  conditionSpec,
  fieldsRequiredFor,
  type ConditionField,
  type ConditionSpec,
} from "../../studio/conditions"
import { normalizeCas, rowIdentity } from "../mapping/conditionRowMerge"
import { useReviewMode } from "../reviewMode"
import type { StatementCard } from "../useStudioV2State"
import { EditableField, isIsoCalendarDate } from "./EditableField"
import {
  EXEMPTION_FIELD,
  OTHER_EXEMPTION,
  entriesInText,
  exemptionLabel,
  exemptionWarning,
  registerEntry,
} from "./exemptionRegister"
import { MemoryAnnotation } from "./MemoryAnnotation"
import { PagedArticleRows, type ArticleRowProps } from "./PagedArticleRows"
import { substanceDisplayName } from "./substanceDisplayName"
import {
  cellState,
  concentrationDisplay,
  concentrationRequired,
  concentrationSuggestionValue,
  entryArticles,
  fieldAddressesFor,
  openCounts,
  substanceEntries,
  withNamedSlots,
  type FieldAddress,
  type SubstanceEntry,
} from "./substanceTable"
import { UnresolvedSubstanceNotice } from "./UnresolvedSubstances"
import {
  skippedCount,
  type ConditionFieldWrites,
  type FieldSaveError,
  type SkippedArticles,
  type SubstancePick,
} from "./useConditionFieldWrites"

type Translate = (
  key: TranslationKeys,
  v?: Record<string, string | number>,
) => string

export interface SubstanceEditorProps {
  declId: string
  card: StatementCard
  /** The supplier pool is empty: the empty-state copy must blame that, not
   *  an unresolved scope. */
  poolEmpty?: boolean
  /** Article number -> name, for the per-article rows and the article
   *  checklist. Missing: bare numbers. */
  articleNames?: Map<string, string>
  /** The substance (`SubstanceEntry.key`) whose per-article rows start
   *  open: the chip the reviewer clicked in the STOFFE row. */
  focusKey?: string | null
  /** This statement's `useConditionFieldWrites`, owned by the sheet so its
   *  "Bestätigen" waits on the same save chain the fields here write through. */
  writes: ConditionFieldWrites
}

/** The registry's bare key for a picked substance, the same spelling the
 *  backend's `AddressBook` stores rows under: the CAS when it has one, else
 *  the universal-substance id. */
export function registryKey(s: Pick<SubstanceOut, "id" | "casNumber">): string {
  return normalizeCas(s.casNumber) ?? s.id
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/

/**
 * The substance editor behind the STOFFE row (review-mask prototype, rounds
 * 5-7; Pablo, 2026-09-16): ONE CARD PER SUBSTANCE the statement declares,
 * stacked vertically. Round 7 replaced round 5's wide table: its field
 * columns (up to five per regime) forced a horizontal scroll inside the
 * half-width pane and pushed the article binding, the connection the
 * reviewer most needs to see, off screen first. The card's header is the
 * glance layer (name, CAS, the concentration, the binding, a complete/open
 * badge); the fields sit behind it at full pane width, so prose fields
 * (safe-use info, warning text) finally have room. Built for the two cases
 * a declaration actually presents and for reviewing what the extraction
 * already filled:
 *
 *  - "the following articles contain 0.1 % lead": one card, one value, one
 *    save. A value typed in a card's shared field lands on EVERY article of
 *    that substance (`commitMany`'s fan-out), so the reviewer never types
 *    the same number twenty times.
 *  - a table in the declaration, one concentration per article: the
 *    per-article toggle opens one quiet row per article; a value typed
 *    there lands on that article alone. A substance whose articles already
 *    disagree reads "n Werte" in the header, starts open with its
 *    per-article rows showing, so each deviation is on screen unprompted.
 *  - the happy path (extraction filled everything): every card collapses to
 *    its one complete line, the header says so, and a probe test is one
 *    click open, read against the PDF beside it, one click closed.
 *
 * THE DATA MODEL IS UNCHANGED (see `substanceTable.ts`): per-article rows
 * plus the reviewer binding, written through the serialised save chain in
 * `useConditionFieldWrites`. Which articles a
 * substance applies to IS the reviewer binding; "+ Stoff" adds a substance
 * as a binding over every resolved article, which is also why only a
 * substance added that way can be removed again (the record has no
 * tombstone for an extracted disclosure).
 */
export function SubstanceEditor({
  declId,
  card,
  poolEmpty = false,
  articleNames,
  focusKey = null,
  writes,
}: SubstanceEditorProps) {
  const { translate } = useTranslation()
  const spec = conditionSpec(card.framework)
  const {
    data,
    rows,
    reviewerBindings,
    memoryHints,
    commitMany,
    commitBinding,
    commitSubstance,
    revertVersions,
    fieldErrors,
    busyDrafts,
    substancePicks,
  } = writes
  const queryClient = useQueryClient()
  // A substance's articles decide which saved exemptions settle its proposal.
  const refreshProposalsAfter = (save: Promise<unknown>) =>
    refreshExemptionsAfter(queryClient, declId, save)
  const articles = card.editorArticles ?? card.resolvedArticles
  const findings = useMemo(
    () => card.conditionDataGaps ?? [],
    [card.conditionDataGaps],
  )
  const substanceLess = spec.concentration === null

  // The run's disclosures, then the slots a reviewer has named since (the
  // substance pick, read back from the record, TUR-1725).
  const entries = useMemo(
    () =>
      withNamedSlots(
        substanceEntries(card.conditions, reviewerBindings, substanceLess),
        articles,
        reviewerBindings,
        card.namedSlots,
      ),
    [
      card.conditions,
      reviewerBindings,
      substanceLess,
      articles,
      card.namedSlots,
    ],
  )
  const { doubt, dataGap } = useMemo(
    () =>
      openCounts(
        entries,
        (entry) => entryArticles(entry, articles, reviewerBindings),
        card,
        data?.rows ?? [],
        findings,
      ),
    // `data?.rows`, react-query's stable reference, never the fresh `rows`
    // array (the same memo hazard `useConditionFieldWrites` documents).
    [entries, articles, reviewerBindings, card, data?.rows, findings],
  )
  const openDoubt = fieldsRequiredFor(card.verdict) ? doubt : 0
  // W5-5: the SAME fold, scoped to one entry, for each card's own header
  // badge (`SubstanceBlock`'s `counts`), computed HERE, once per editor
  // render, never inside `SubstanceBlock` itself: that used to call
  // `openCounts` unmemoised on every one of its own renders (parent
  // re-renders included), the exact anti-pattern this wave's fold-once fix
  // closed for the banners above.
  const perEntryCounts = useMemo(() => {
    const map = new Map<string, ReturnType<typeof openCounts>>()
    for (const entry of entries) {
      map.set(
        entry.key,
        openCounts(
          [entry],
          (e) => entryArticles(e, articles, reviewerBindings),
          card,
          data?.rows ?? [],
          findings,
        ),
      )
    }
    return map
  }, [entries, articles, reviewerBindings, card, data?.rows, findings])

  // Each entry's articles, once per scope/binding change: the same array on
  // every render, so `SubstanceBlock`'s per-field cell states (one value read
  // per article per field) are not redone on unrelated re-renders.
  const articlesByEntry = useMemo(() => {
    const map = new Map<string, string[]>()
    for (const entry of entries)
      map.set(entry.key, entryArticles(entry, articles, reviewerBindings))
    return map
  }, [entries, articles, reviewerBindings])

  // The substances the reviewer picked in THIS mount, by registry key, so
  // an added row names itself before (and without) a registry round trip.
  const [known, setKnown] = useState<Record<string, SubstanceOut>>({})
  // Per-substance open/closed overrides; the default is computed per render
  // (a row whose values differ per article starts open, as does the chip's
  // own substance).
  const [toggles, setToggles] = useState<Record<string, boolean>>({})

  const presentKeys = useMemo(
    () => new Set(entries.map((e) => e.key)),
    [entries],
  )

  if (articles.length === 0) {
    return (
      <p className="text-[11.5px] leading-normal text-grey5">
        {translate(
          poolEmpty
            ? "MC_DECL_V2_ARTS_POOL_EMPTY"
            : "MC_DECL_V2_COND_NO_ARTICLES",
        )}
      </p>
    )
  }

  // The reviewer decides which articles a substance applies to. "All" on an
  // extracted, article-agnostic substance is the default and is written as
  // NO reviewer binding at all (`withBinding([])` removes it); an added
  // substance, one the extraction itself bound narrower, or one only a named
  // slot carries (`picked`), needs the explicit full list to mean "all".
  //
  // N9 POOL FALLBACK (`flaggedOnly`): for a scopeAll statement with an empty
  // resolution, `articles` is `card.editorArticles`, i.e. only the pool
  // articles the gate flags, and the labels say "flagged articles", not
  // "all". An explicit binding stored in this state IS that flagged set: it
  // stays this narrow once the resolution arrives (it is a persisted
  // article list, never re-expanded); only the no-binding default follows
  // the resolution.
  const commitArticles = (entry: SubstanceEntry, next: string[]) => {
    if (!entry.cas || next.length === 0) return
    const everything = next.length === articles.length
    const write =
      everything && !entry.added && !entry.picked && !entry.serverBound
        ? []
        : next
    void refreshProposalsAfter(
      commitBinding(card.framework, card.id, entry.cas, write),
    )
  }
  const removeAdded = (entry: SubstanceEntry) => {
    if (entry.cas) {
      void refreshProposalsAfter(
        commitBinding(card.framework, card.id, entry.cas, []),
      )
    }
  }
  const addSubstance = (s: SubstanceOut) => {
    const key = registryKey(s)
    setKnown((prev) => ({ ...prev, [key]: s }))
    void refreshProposalsAfter(
      commitBinding(card.framework, card.id, key, [...articles]),
    )
  }

  return (
    <div className="sv2-stab-wrap" data-testid="substance-table">
      {openDoubt > 0 && (
        <div className="sv2-cfopen sv2-evgap" role="status" tabIndex={0}>
          <TriangleAlert className="sv2-ic" />
          <span>
            {translate(
              openDoubt === 1
                ? "MC_DECL_V2_COND_OPEN_ONE"
                : "MC_DECL_V2_COND_OPEN_MANY",
              { n: openDoubt },
            )}
          </span>
        </div>
      )}
      {dataGap > 0 && (
        <div
          data-testid="cfeditor-data-gap"
          className="sv2-cfopen sv2-evgap sv2-datagap"
          role="status"
          tabIndex={0}
        >
          <Info className="sv2-ic" />
          <span>
            {translate(
              dataGap === 1
                ? "MC_DECL_V2_COND_GAP_ONE"
                : "MC_DECL_V2_COND_GAP_MANY",
              { n: dataGap },
            )}
          </span>
        </div>
      )}
      {!fieldsRequiredFor(card.verdict) && (
        <div
          data-testid="cfeditor-not-required-hint"
          className="sv2-cfopen sv2-evgap sv2-datagap"
          role="status"
          tabIndex={0}
        >
          <Info className="sv2-ic" />
          <span>{translate("MC_DECL_V2_COND_NOT_REQUIRED_HINT")}</span>
        </div>
      )}
      {fieldsRequiredFor(card.verdict) &&
        openDoubt === 0 &&
        dataGap === 0 &&
        entries.length > 0 && (
          <div
            data-testid="substances-complete"
            className="sv2-cfopen sv2-evgap sv2-scomplete"
            role="status"
            tabIndex={0}
          >
            <Check className="sv2-ic" />
            <span>{translate("MC_DECL_V2_SUBST_ALL_COMPLETE")}</span>
          </div>
        )}
      <div className="sv2-scards">
        {entries.map((entry) => {
          const entryArts =
            articlesByEntry.get(entry.key) ??
            entryArticles(entry, articles, reviewerBindings)
          return (
            <SubstanceBlock
              key={entry.key}
              entry={entry}
              entryArticles={entryArts}
              allArticles={articles}
              articleNames={articleNames}
              card={card}
              spec={spec}
              rows={rows}
              findings={findings}
              memoryHints={memoryHints}
              declId={declId}
              known={known[entry.key]}
              open={toggles[entry.key]}
              focused={entry.key === focusKey}
              onToggle={(next) =>
                setToggles((prev) => ({ ...prev, [entry.key]: next }))
              }
              revertVersions={revertVersions}
              fieldErrors={fieldErrors}
              busyDrafts={busyDrafts}
              commitMany={commitMany}
              counts={perEntryCounts.get(entry.key)!}
              onCommitArticles={(next) => commitArticles(entry, next)}
              onRemove={entry.added ? () => removeAdded(entry) : undefined}
              pickState={substancePicks[entry.key]}
              onPickSubstance={(s) =>
                void refreshProposalsAfter(
                  commitSubstance(entry.key, entryArts, {
                    id: s.id,
                    name: s.name,
                    casNumber: s.casNumber,
                  }),
                )
              }
              translate={translate}
            />
          )
        })}
      </div>
      {!substanceLess && (
        <PermissionGate permission={PERMISSIONS.MC_DECLARATIONS_REVIEW}>
          <div className="sv2-stab-addwrap">
            <RegistrySearch
              exclude={presentKeys}
              onPick={addSubstance}
              label={translate("MC_DECL_V2_SUBST_ADD")}
              icon={<Plus className="sv2-ic" />}
              translate={translate}
            />
          </div>
        </PermissionGate>
      )}
    </div>
  )
}

/** N9: the editor is on the pool fallback, its articles are only the
 *  gate-flagged pool articles (`StatementCard.editorArticles`) of a scopeAll
 *  statement whose resolution is empty, never the statement's whole scope. */
function flaggedOnly(card: StatementCard): boolean {
  return (
    card.scopeAll &&
    card.articleNumbers.length === 0 &&
    card.resolvedArticles.length === 0 &&
    (card.editorArticles?.length ?? 0) > 0
  )
}

type MemoryHint = SubstanceBlockProps["memoryHints"][number]

/** The most articles whose per-article rows open by themselves (focused chip,
 *  differing values). Above it they open only on the toggle. */
export const AUTO_OPEN_ARTICLES_MAX = 200

interface SubstanceBlockProps {
  entry: SubstanceEntry
  /** The articles THIS substance applies to (`entryArticles`). */
  entryArticles: string[]
  /** The statement's whole resolved scope, the checklist's universe. */
  allArticles: readonly string[]
  articleNames?: Map<string, string>
  card: StatementCard
  spec: ConditionSpec
  rows: readonly ConditionFieldRow[]
  findings: NonNullable<StatementCard["conditionDataGaps"]>
  memoryHints: ConditionFieldWrites["memoryHints"]
  declId: string
  /** The registry row the reviewer picked for an added substance, when it
   *  was picked in this mount. */
  known?: SubstanceOut
  /** The reviewer's own open/closed choice; undefined = the default. */
  open?: boolean
  focused: boolean
  onToggle: (next: boolean) => void
  revertVersions: Record<string, number>
  fieldErrors: Record<string, FieldSaveError>
  /** A busy field's kept draft by identity (`useConditionFieldWrites`). */
  busyDrafts: Record<string, string>
  commitMany: (edits: ConditionFieldRow[]) => Promise<unknown>
  /** This entry's own open-count fold, computed ONCE per editor render in
   *  the parent's `perEntryCounts` memo, never recomputed inside this
   *  component (W5-5 fold-once fix). */
  counts: ReturnType<typeof openCounts>
  onCommitArticles: (next: string[]) => void
  /** Only an added substance can be removed (its binding IS its presence).
   *  Its X sits at the header's right end, open or collapsed, the same
   *  corner as the statement card's own remove X. */
  onRemove?: () => void
  pickState?: SubstancePick
  onPickSubstance: (s: SubstanceOut) => void
  translate: Translate
}

/** One substance's card: a glance header (name, CAS, the concentration, the
 *  article binding, a complete/open badge), the framework's fields stacked
 *  full-width behind it, and one quiet sub-row per article behind the
 *  per-article toggle. */
function SubstanceBlock({
  entry,
  entryArticles: articles,
  allArticles,
  articleNames,
  card,
  spec,
  rows,
  findings,
  memoryHints,
  declId,
  known,
  open,
  focused,
  onToggle,
  revertVersions,
  fieldErrors,
  busyDrafts,
  commitMany,
  counts,
  onCommitArticles,
  onRemove,
  pickState,
  onPickSubstance,
  translate,
}: SubstanceBlockProps) {
  const exemptions = useDeclarationExemptions(declId)
  const queryClient = useQueryClient()
  // Step C4 (`MC_REVIEW_PAGED`, only where the tables are on): a substance above
  // `AUTO_OPEN_ARTICLES_MAX` gets its per-article rows one page at a time and only
  // the rows in view; every other table renders exactly as before.
  // Read once per page mount on the review page (`ReviewMode.pagedTables`), so a
  // flag refresh mid-session never swaps a table's source under the reviewer.
  const livePaged = useReviewPaged()
  const reviewPaged = useReviewMode().pagedTables ?? livePaged
  // The paged table's scroll box, held in state (a callback ref): the virtualizer
  // attaches to it on the render after it mounts, which a plain ref never triggers.
  const [tableScrollEl, setTableScrollEl] = useState<HTMLDivElement | null>(
    null,
  )
  // `rows` is react-query's stable reference and `articles` the parent's
  // memoised list: recomputed only when either changes.
  const cells = useMemo(
    () =>
      fieldAddressesFor(spec, card.framework, entry.cas).map((address) => ({
        address,
        cell: cellState(rows, articles, address),
      })),
    [spec, card.framework, entry.cas, rows, articles],
  )
  const anyMixed = cells.some(({ cell }) => cell.shared === null)
  // A card that still needs the reviewer (an open required field, values
  // that differ per article, the chip they clicked) starts open; a complete
  // one starts as its one-line summary: the happy path stays a skim.
  const exemptionRegister = exemptions.entries.filter(
    (e) => e.framework === card.framework,
  )
  // A stored exemption that is no register entry, or an expired one, is filled but
  // wrong: it starts open so its warning is seen, like a missing value would.
  const invalidExemption =
    exemptionRegister.length > 0 &&
    cells.some(
      ({ address, cell }) =>
        address.fieldKey === EXEMPTION_FIELD &&
        !!cell.shared &&
        exemptionWarning(
          registerEntry(cell.shared, exemptionRegister),
          cell.shared,
          translate,
        ) !== undefined,
    )
  const isOpen =
    open ?? (anyMixed || focused || counts.doubt > 0 || invalidExemption)
  const perArticle = articles.length > 1
  // The per-article rows' own toggle, inside the open card: differing values
  // (and the focused chip) start them open, a uniform substance keeps them
  // one click away.
  // Held with the mode it was set in: rows a reviewer opened on the paged table
  // must not stay open, unpaged, if the flag goes off under them (every article
  // would render at once, the very freeze the cap exists for).
  const [artsToggleState, setArtsToggleState] = useState<
    { open: boolean; paged: boolean } | undefined
  >(undefined)
  // Never on its own above `AUTO_OPEN_ARTICLES_MAX`: the rows render every
  // article at once, and a 9,999-article substance froze the page when the
  // focused chip opened them (sandbox DEC-2026-0007). The toggle still opens
  // them on request; paging them is Step C4.
  const capped = perArticle && articles.length > AUTO_OPEN_ARTICLES_MAX
  // Paged (C4) only where the server's list IS the editor's: the endpoint pages
  // the statement's resolved articles, so a scopeAll statement whose editor list
  // is its own subset or the flagged-pool fallback (`editorArticles`, N9) keeps
  // the unpaged table rather than a paged one that would come back empty.
  const paged =
    reviewPaged &&
    capped &&
    (card.editorArticles == null ||
      card.editorArticles === card.resolvedArticles)
  // Paged rows are cheap to open (only the rows in view are drawn), so the cap
  // holds them shut only on the unpaged table.
  const artsToggle =
    artsToggleState && artsToggleState.paged === paged
      ? artsToggleState.open
      : undefined
  const artsOpen =
    perArticle && (artsToggle ?? ((!capped || paged) && (anyMixed || focused)))
  // The rows WOULD have opened by themselves and the cap alone kept them shut:
  // said in the card, so a focused chip or differing values never land on a
  // closed table with no sign why.
  const heldByCap =
    capped && !paged && artsToggle === undefined && (anyMixed || focused)
  // A shared field whose articles carry DIFFERENT values writes one value over
  // all of them. With the rows open the reviewer sees what goes; with the rows
  // shut by the cap they do not, so the write waits for an explicit "set for
  // all" (in the card, never a browser dialog). `nonce` remounts the field on
  // cancel, back to its "n values" placeholder, and focus goes back into it.
  // Only the address and the value are held: the articles it goes to and
  // whether their values still differ are read off the CURRENT rows when the
  // confirmation renders and when it is applied, so a reload in between never
  // writes to a stale article list or shows a stale warning.
  // One waiting write PER FIELD (keyed by `fieldKeyOf`): a second field typed
  // while the first still waits keeps both, each with its own confirmation.
  // A newer edit of the SAME field (waiting or saved, shared or one article's)
  // replaces or drops the older one, so a stale "set for all" can never
  // overwrite a value saved after it. The nonce is per field too: cancelling
  // one field remounts only that field, never another one's typed text.
  const [pendingFanouts, setPendingFanouts] = useState<
    Record<string, { address: FieldAddress; value: string; refresh: boolean }>
  >({})
  const [fanoutNonces, setFanoutNonces] = useState<Record<string, number>>({})
  const dropPendingFanout = (key: string) =>
    setPendingFanouts((current) => {
      if (!(key in current)) return current
      const rest = { ...current }
      delete rest[key]
      return rest
    })
  const pendingCount = Object.keys(pendingFanouts).length
  const fieldWraps = useRef(new Map<string, HTMLDivElement>())
  const refocusField = useRef<string | null>(null)
  const fanoutTextId = useId()
  useEffect(() => {
    const key = refocusField.current
    if (key === null) return
    refocusField.current = null
    fieldWraps.current
      .get(key)
      ?.querySelector<HTMLElement>("input, select, textarea")
      ?.focus()
  }, [fanoutNonces])
  const commitFanout = (
    address: FieldAddress,
    target: readonly string[],
    value: string,
    refresh: boolean,
  ) => {
    const settled = commitMany(
      target.map((articleNumber) => ({ articleNumber, ...address, value })),
    )
    if (refresh) void refreshExemptionsAfter(queryClient, declId, settled)
    return settled
  }
  // Memory's hints per (article, CAS), first hint wins as `find` did: looked
  // up once per rendered row instead of scanning every hint per row.
  const hintAt = useMemo(() => {
    const byArticle = new Map<string, Map<string | null, MemoryHint>>()
    for (const h of memoryHints) {
      const cas = normalizeCas(h.substanceCas) ?? null
      let byCas = byArticle.get(h.articleNumber)
      if (!byCas) {
        byCas = new Map()
        byArticle.set(h.articleNumber, byCas)
      }
      if (!byCas.has(cas)) byCas.set(cas, h)
    }
    return (articleNumber: string, cas: string | null) =>
      byArticle.get(articleNumber)?.get(cas)
  }, [memoryHints])

  const fieldKeyOf = (address: FieldAddress) =>
    `${address.framework ?? ""}:${address.fieldKey}`
  const identityOf = (articleNumber: string, address: FieldAddress) =>
    rowIdentity({ articleNumber, ...address, value: "" })
  const revertKey = (articleNumber: string, address: FieldAddress) => {
    const identity = identityOf(articleNumber, address)
    return `${identity}:${revertVersions[identity] ?? 0}`
  }
  const fieldOf = (address: FieldAddress): ConditionField | null =>
    address.framework === null
      ? null
      : (spec.fields.find((f) => f.key === address.fieldKey) ?? null)
  const labelOf = (address: FieldAddress) =>
    address.framework === null
      ? translate(spec.concentration!.label)
      : translate(fieldOf(address)!.label)
  const extractedOf = (address: FieldAddress) =>
    address.framework === null
      ? entry.concentrations
      : (entry.fieldValues[address.fieldKey] ?? [])
  const requiredOn = (articleNumber: string, address: FieldAddress) =>
    address.framework === null
      ? concentrationRequired(
          spec,
          card.verdict,
          rows,
          findings,
          card.framework,
          articleNumber,
          entry.cas,
        )
      : fieldsRequiredFor(card.verdict) && !!fieldOf(address)?.required
  const kindOn = (value: string, address: FieldAddress): "text" | "date" =>
    fieldOf(address)?.kind === "date" &&
    (value === "" || isIsoCalendarDate(value))
      ? "date"
      : "text"
  /** A `"select"` field's choices, labels translated (cp65 `endpoint`). */
  const optionsOn = (address: FieldAddress) => {
    const field = fieldOf(address)
    return field?.kind === "select"
      ? field.options?.map((o) => ({
          value: o.value,
          label: translate(o.label),
        }))
      : undefined
  }

  /** One editable field: the card's shared one (every article, `shared`) or
   *  one article's (`compact`, inside the per-article table, whose column
   *  head is the visible label). Same hint / one-click-accept /
   *  rejected-fragment rules the per-article grid applied before wave 5.
   *  `target[0]` is the anchor: the cell reads its required/error
   *  bookkeeping off it, and a fan-out commit leads with it (`commitMany`
   *  anchors revert/error on `edits[0]`). */
  const renderField = (
    address: FieldAddress,
    target: readonly string[],
    value: string,
    mixedCount: number | null,
    compact: boolean,
  ) => {
    const anchor = target[0]
    const extracted = extractedOf(address)
    const kind = kindOn(value, address)
    const mixed = mixedCount !== null
    // THE EXEMPTION FIELD IS A REGISTER PICKER wherever the framework has a register
    // (RoHS Annex III/IV): its entries are the choices, the proposed entry is the
    // one-click suggestion, and free text is only "other", with a warning.
    const register =
      address.fieldKey === EXEMPTION_FIELD && address.framework
        ? exemptionRegister
        : []
    const picker = register.length > 0
    const options = picker
      ? register.map((e) => ({
          value: e.entryNumber,
          label: exemptionLabel(e, translate),
        }))
      : optionsOn(address)
    const proposed = picker
      ? exemptions.suggestions.find(
          (x) =>
            x.framework === address.framework &&
            x.statementId === card.id &&
            normalizeCas(x.substanceCas) === entry.cas,
        )
      : undefined
    const proposedEntry = proposed
      ? registerEntry(proposed.entryNumber, register)
      : null
    const hint =
      !mixed && value === "" && extracted.length > 0
        ? extracted.join(" · ")
        : undefined
    const rejected = entry.rejectedValues[address.fieldKey] ?? []
    return (
      <div
        className={cn(mixed && "sv2-stab-mixedcell")}
        ref={
          compact
            ? undefined
            : (el) => {
                const key = fieldKeyOf(address)
                if (el) fieldWraps.current.set(key, el)
                else fieldWraps.current.delete(key)
              }
        }
      >
        <EditableField
          key={`${revertKey(anchor, address)}:${mixed ? "mixed" : value}:${fanoutNonces[fieldKeyOf(address)] ?? 0}`}
          compact={compact}
          label={labelOf(address)}
          hint={
            compact || address.framework === null
              ? undefined
              : fieldOf(address)?.hint
                ? translate(fieldOf(address)!.hint!)
                : undefined
          }
          required={!mixed && requiredOn(anchor, address)}
          value={value}
          kind={kind}
          options={options}
          placeholder={
            mixed
              ? translate("MC_DECL_V2_SUBST_MIXED", { n: mixedCount })
              : hint
          }
          otherOption={
            picker
              ? {
                  value: OTHER_EXEMPTION,
                  label: translate("MC_DECL_V2_EXEMPTION_OTHER"),
                  warning: translate("MC_DECL_V2_EXEMPTION_OTHER_WARNING"),
                }
              : undefined
          }
          warning={
            picker && !mixed && value !== ""
              ? exemptionWarning(
                  registerEntry(value, register),
                  value,
                  translate,
                )
              : undefined
          }
          suggestionNote={
            proposedEntry
              ? translate(
                  proposed!.source === "document"
                    ? "MC_DECL_V2_EXEMPTION_FROM_DOCUMENT"
                    : "MC_DECL_V2_EXEMPTION_SUGGESTED",
                  {
                    code: proposedEntry.entryNumber,
                    scope: proposedEntry.substanceScope ?? "",
                  },
                )
              : undefined
          }
          suggestion={
            picker
              ? !mixed && value === "" && proposedEntry
                ? proposedEntry.entryNumber
                : undefined
              : !mixed &&
                  value === "" &&
                  extracted.length === 1 &&
                  // A dropdown only takes one of its own options in one click; any
                  // other extracted text stays a hint beside it, never a stored value.
                  (options
                    ? options.some((o) => o.value === extracted[0])
                    : kind !== "date" || isIsoCalendarDate(extracted[0]))
                ? // THE CONCENTRATION CELL ONLY (`framework: null`, fieldKey
                  // "concentration"): its unit is already per-cent, so a trailing "%" on the
                  // extracted string is noise that makes the saved value unparseable by both
                  // the master-data write and Turnus Memory. Every other field keeps the
                  // document's text exactly as extracted.
                  address.fieldKey === "concentration" &&
                  address.framework === null
                  ? concentrationSuggestionValue(extracted[0])
                  : extracted[0]
                : undefined
          }
          rejectedHint={
            !mixed && value === "" && rejected.length > 0
              ? translate("MC_DECL_V2_COND_REJECTED_HINT", {
                  value: rejected.join(", "),
                })
              : undefined
          }
          error={fieldErrors[identityOf(anchor, address)] ?? false}
          draft={
            fieldErrors[identityOf(anchor, address)] === "busy"
              ? busyDrafts[identityOf(anchor, address)]
              : undefined
          }
          onCommit={(next) => {
            // A typed text naming exactly one register entry is stored as that entry's own
            // number; anything else (no code, several) as typed, flagged by the warning.
            const named = picker ? entriesInText(next, register) : []
            const stored = picker
              ? named.length === 1
                ? named[0].entryNumber
                : next.trim()
              : next
            // A cleared exemption is open again, and a new concentration changes what
            // fits: ask for a proposal once it is saved.
            const refresh =
              (picker && stored === "") ||
              (address.framework === null &&
                address.fieldKey === "concentration")
            const key = fieldKeyOf(address)
            // Paged rows show ~10 of thousands of articles, so a write over
            // differing values waits for "set for all" there too.
            if (!compact && mixed && capped && (!artsOpen || paged))
              setPendingFanouts((current) => ({
                ...current,
                [key]: { address, value: stored, refresh },
              }))
            else {
              dropPendingFanout(key)
              void commitFanout(address, target, stored, refresh)
            }
          }}
        />
      </div>
    )
  }

  const bindingError =
    entry.cas != null &&
    (fieldErrors[
      rowIdentity({
        articleNumber: "__binding__",
        substanceCas: entry.cas,
        framework: card.framework,
        fieldKey: "__connected__",
        value: "",
      })
    ] ??
      false)

  const total = allArticles.length
  const flagged = flaggedOnly(card)
  const countLabel =
    articles.length === total
      ? translate(
          flagged
            ? "MC_DECL_V2_HEAD_COUNT_ALL_FLAGGED"
            : "MC_DECL_V2_HEAD_COUNT_ALL",
          { total },
        )
      : translate(
          flagged
            ? "MC_DECL_V2_HEAD_COUNT_OF_FLAGGED"
            : "MC_DECL_V2_HEAD_COUNT_OF",
          { n: articles.length, total },
        )

  let nameNode: ReactNode
  if (entry.names.length > 0) {
    nameNode = [
      ...new Set(
        entry.names.map((n) => substanceDisplayName(n, entry.cas, translate)),
      ),
    ].join(", ")
  } else if (entry.cas) {
    nameNode = known ? (
      substanceDisplayName(known.name, known.casNumber, translate)
    ) : (
      <RegistryName bare={entry.cas} translate={translate} />
    )
  } else if (spec.concentration === null) {
    nameNode = "-"
  } else {
    // The extraction named no substance and left no CAS (TUR-1211, W4-1b):
    // the reviewer picks one; the server rekeys the slot's values.
    nameNode = (
      <PermissionGate permission={PERMISSIONS.MC_DECLARATIONS_REVIEW}>
        <PickSubstanceControl
          pickState={pickState}
          onPick={onPickSubstance}
          translate={translate}
        />
      </PermissionGate>
    )
  }

  const requiredMark = fieldsRequiredFor(card.verdict) && (
    <span className="sv2-req" aria-hidden="true">
      {" "}
      *
    </span>
  )
  const toggleLabel = translate(
    isOpen ? "MC_DECL_V2_SUBST_CARD_COLLAPSE" : "MC_DECL_V2_SUBST_CARD_EXPAND",
  )
  // One article's sub-row, the same markup paged (C4) or not: the values come
  // from the condition rows every save writes into; `name` is the paged read's
  // own, else the editor's article names.
  const articleRow = (
    articleNumber: string,
    name: string | null,
    rowProps?: ArticleRowProps,
  ) => (
    <tr
      key={rowProps ? `${rowProps["data-index"]}` : articleNumber}
      className="sv2-stab-sub"
      data-testid="substance-table-article"
      {...rowProps}
    >
      <td className="sv2-stab-name">
        <div className="sv2-stab-art">
          <span className="sv2-stab-artno">{articleNumber}</span>
          {(name ?? articleNames?.get(articleNumber)) && (
            <span
              className="sv2-stab-artname"
              title={name ?? articleNames?.get(articleNumber)}
            >
              {name ?? articleNames?.get(articleNumber)}
            </span>
          )}
        </div>
      </td>
      {cells.map(({ address, cell }) => {
        const value = cell.values.get(articleNumber) ?? ""
        // Turnus Memory annotates ONE article's cell,
        // never a fan-out (the rule the per-article
        // grid set): only here, in the per-article row.
        const hint =
          address.framework === null
            ? hintAt(articleNumber, entry.cas)
            : undefined
        return (
          <td key={address.fieldKey} className="sv2-stab-val">
            {renderField(address, [articleNumber], value, null, true)}
            {hint && <MemoryAnnotation declId={declId} hint={hint} />}
          </td>
        )
      })}
    </tr>
  )

  const artsToggleLabel = artsOpen
    ? translate("MC_DECL_V2_SUBST_COLLAPSE")
    : capped && !paged
      ? translate("MC_DECL_V2_SUBST_EXPAND_MANY", { n: articles.length })
      : translate("MC_DECL_V2_SUBST_EXPAND")

  // The glance value beside the binding: the ONE persisted concentration, or
  // how many values the articles carry: read-only text, the skim layer.
  const concCell = spec.concentration
    ? cells.find(({ address }) => address.framework === null)
    : undefined
  const badge =
    counts.doubt > 0 ? (
      <span className="sv2-scard-badge sv2-scard-badge-open">
        {translate("MC_DECL_V2_SUBST_CARD_OPEN", { n: counts.doubt })}
      </span>
    ) : counts.dataGap > 0 ? (
      <span className="sv2-scard-badge sv2-scard-badge-gap">
        <Info className="sv2-ic" />
        {translate("MC_DECL_V2_SUBST_CARD_GAP")}
      </span>
    ) : fieldsRequiredFor(card.verdict) ? (
      <span className="sv2-scard-badge sv2-scard-badge-done">
        <Check className="sv2-ic" />
        {translate("MC_DECL_V2_SUBST_CARD_COMPLETE")}
      </span>
    ) : null

  return (
    <div className="sv2-scard" data-testid="substance-table-row">
      {/* The whole header is the toggle's click target; the interactive
          bits inside it (binding popover, substance pick, remove X) stop
          the click. */}
      <div className="sv2-scard-head" onClick={() => onToggle(!isOpen)}>
        <button
          type="button"
          className="sv2-stab-chev"
          aria-expanded={isOpen}
          aria-label={toggleLabel}
          title={toggleLabel}
          onClick={(e) => {
            e.stopPropagation()
            onToggle(!isOpen)
          }}
        >
          <ChevronDown size={14} />
        </button>
        <div className="sv2-stab-sid">
          <span className="sv2-stab-sname">
            {nameNode}
            {entry.added && (
              <span className="sv2-stab-tag">
                {translate("MC_DECL_V2_SUBST_ADDED_TAG")}
              </span>
            )}
            {entry.cas && (
              <span
                className="sv2-stab-scas"
                title={translate("MC_DECL_V2_COND_CAS_LABEL_TITLE")}
              >
                {entry.cas}
              </span>
            )}
          </span>
          {entry.locations.length > 0 && (
            <span className="sv2-stab-sloc">{entry.locations.join(", ")}</span>
          )}
        </div>
        <div className="sv2-scard-glance" onClick={(e) => e.stopPropagation()}>
          {concCell &&
            (concCell.cell.shared === null ? (
              <span className="sv2-scard-val sv2-scard-val-mixed">
                {translate("MC_DECL_V2_SUBST_MIXED", {
                  n: concCell.cell.distinct.length,
                })}
              </span>
            ) : (
              concCell.cell.shared !== "" && (
                <span className="sv2-scard-val">
                  {concentrationDisplay(concCell.cell.shared)}
                </span>
              )
            ))}
          {entry.cas ? (
            <PermissionGate
              permission={PERMISSIONS.MC_DECLARATIONS_REVIEW}
              fallback={
                <span className="sv2-stab-bind-plain">{countLabel}</span>
              }
            >
              <ArticleBindPopover
                label={countLabel}
                partial={articles.length < total}
                all={allArticles}
                allLabel={translate(
                  flagged
                    ? "MC_DECL_V2_COND_BIND_ALL_FLAGGED"
                    : "MC_DECL_V2_COND_BIND_ALL",
                )}
                selected={articles}
                articleNames={articleNames}
                onCommit={onCommitArticles}
                translate={translate}
              />
            </PermissionGate>
          ) : (
            <span className="sv2-stab-bind-plain">{countLabel}</span>
          )}
          {pendingCount > 0 && !isOpen && (
            // A "set for all" still waiting when the card was collapsed: it
            // stays waiting (and shows again on reopen), never saved or
            // dropped behind the reviewer's back.
            <span
              className="sv2-scard-badge sv2-scard-badge-open"
              data-testid="substance-fanout-pending"
            >
              {translate("MC_DECL_V2_SUBST_FANOUT_PENDING", {
                n: pendingCount,
              })}
            </span>
          )}
          {badge}
          {onRemove && (
            <PermissionGate permission={PERMISSIONS.MC_DECLARATIONS_REVIEW}>
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label={translate("MC_DECL_V2_SUBST_REMOVE")}
                      className="sv2-exdel"
                      onClick={onRemove}
                    >
                      <X className="sv2-ic" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    {translate("MC_DECL_V2_SUBST_REMOVE")}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </PermissionGate>
          )}
        </div>
      </div>
      {entry.unresolved.length > 0 && (
        <UnresolvedSubstanceNotice
          declId={declId}
          card={card}
          entry={entry}
          name={entry.names[0] ?? ""}
        />
      )}
      {bindingError && (
        <p className="sv2-scard-binderr text-[11px] text-red1">
          {translate("MC_DECL_V2_COND_FIELD_SAVE_FAILED")}
        </p>
      )}
      {isOpen && (
        <div className="sv2-scard-body">
          <div className="sv2-scard-fields">
            {cells.map(({ address, cell }) => {
              /* Turnus Memory, on the substance-level field — but ONLY while this
               * statement covers a single article.
               *
               * WHY THE CONDITION. This field writes to EVERY article in `articles`, and
               * the per-article grid's rule is that accepting a memory must never fan out.
               * With one article, "all articles" IS that article, so there is nothing to
               * fan out to and the rule is satisfied rather than bent.
               *
               * WHY IT IS NEEDED AT ALL. The per-article rows only exist when
               * `articles.length > 1`, so before this the annotation could not render for a
               * single-article statement — the commonest case by far. The hint was fetched,
               * returned over the wire, and dropped on the floor; found on sandbox, where a
               * remembered 0.05 never appeared beside an extracted 0.5. */
              const soleArticleHint =
                !perArticle && address.framework === null
                  ? hintAt(articles[0], entry.cas)
                  : undefined
              return (
                <div key={address.fieldKey}>
                  {renderField(
                    address,
                    articles,
                    cell.shared ?? "",
                    cell.shared === null ? cell.distinct.length : null,
                    false,
                  )}
                  {soleArticleHint && (
                    <MemoryAnnotation declId={declId} hint={soleArticleHint} />
                  )}
                  {pendingFanouts[fieldKeyOf(address)] && (
                    <div
                      className="sv2-scard-fanout"
                      role="group"
                      aria-label={translate("MC_DECL_V2_SUBST_FANOUT_LABEL")}
                      aria-describedby={`${fanoutTextId}-${fieldKeyOf(address)}`}
                      data-testid="substance-fanout-confirm"
                    >
                      <span
                        id={`${fanoutTextId}-${fieldKeyOf(address)}`}
                        role="status"
                      >
                        {translate(
                          // Read off the current rows: values that have since
                          // become equal are no longer "replaced".
                          cell.shared === null
                            ? "MC_DECL_V2_SUBST_FANOUT_CONFIRM"
                            : "MC_DECL_V2_SUBST_FANOUT_CONFIRM_SAME",
                          {
                            n: articles.length,
                            value: pendingFanouts[fieldKeyOf(address)].value,
                          },
                        )}
                      </span>
                      <button
                        type="button"
                        className="sv2-act sv2-primary"
                        onClick={() => {
                          const key = fieldKeyOf(address)
                          const {
                            address: a,
                            value,
                            refresh,
                          } = pendingFanouts[key]
                          dropPendingFanout(key)
                          void commitFanout(a, articles, value, refresh)
                        }}
                      >
                        {translate("MC_DECL_V2_SUBST_FANOUT_APPLY", {
                          n: articles.length,
                        })}
                      </button>
                      <button
                        type="button"
                        className="sv2-act"
                        onClick={() => {
                          const key = fieldKeyOf(address)
                          refocusField.current = key
                          dropPendingFanout(key)
                          setFanoutNonces((current) => ({
                            ...current,
                            [key]: (current[key] ?? 0) + 1,
                          }))
                        }}
                      >
                        {translate("MC_DECL_V2_SUBST_FANOUT_CANCEL")}
                      </button>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
          {perArticle && (
            <div className="sv2-scard-arts">
              <button
                type="button"
                className="sv2-scard-artstoggle"
                aria-expanded={artsOpen}
                onClick={() => setArtsToggleState({ open: !artsOpen, paged })}
              >
                <ChevronDown
                  className={cn("sv2-ic", artsOpen && "sv2-scard-chev-open")}
                />
                {artsToggleLabel}
              </button>
              {heldByCap && !artsOpen && (
                <p
                  className="sv2-scard-artsheld"
                  data-testid="substance-rows-held"
                >
                  {translate(
                    anyMixed
                      ? "MC_DECL_V2_SUBST_ROWS_HELD_MIXED"
                      : "MC_DECL_V2_SUBST_ROWS_HELD",
                    { n: articles.length },
                  )}
                </p>
              )}
              {artsOpen && (
                <div
                  ref={paged ? setTableScrollEl : undefined}
                  className={cn(
                    "sv2-stab-scroll",
                    paged && "sv2-stab-scroll-paged",
                  )}
                >
                  <table
                    className="sv2-stab"
                    // The editor's list: the paged rows match it row for row or
                    // fall back to it, so the count holds while pages load too.
                    aria-rowcount={paged ? articles.length + 1 : undefined}
                  >
                    <thead>
                      <tr aria-rowindex={paged ? 1 : undefined}>
                        <th className="sv2-stab-name">
                          {translate("MC_DECL_V2_SUBST_COL_ARTICLES")}
                        </th>
                        {spec.concentration && (
                          <th className="sv2-stab-val">
                            {translate(spec.concentration.label)}
                            {spec.concentration.required && requiredMark}
                          </th>
                        )}
                        {spec.fields.map((f) => (
                          <th
                            key={f.key}
                            className="sv2-stab-val"
                            title={f.hint ? translate(f.hint) : undefined}
                          >
                            {translate(f.label)}
                            {f.required && requiredMark}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    {paged ? (
                      <PagedArticleRows
                        declId={declId}
                        statementId={card.id}
                        substance={entry.cas ?? ""}
                        scrollElement={tableScrollEl}
                        columnCount={
                          1 + (spec.concentration ? 1 : 0) + spec.fields.length
                        }
                        fallbackArticles={articles}
                        renderRow={articleRow}
                        loadingLabel={translate(
                          "MC_DECL_V2_SUBST_ROWS_LOADING",
                        )}
                      />
                    ) : (
                      <tbody>
                        {articles.map((articleNumber) =>
                          articleRow(articleNumber, null),
                        )}
                      </tbody>
                    )}
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/** The article column's control: the count as a button, the statement's
 *  resolved articles as a checklist behind it, "Alle Artikel" on top. Every
 *  toggle commits at once (the persisted binding is the source of truth,
 *  like `ArticleBindControl`); the last remaining article cannot be
 *  unchecked, a substance applying to no article is not a state this table
 *  offers (an added one is removed with its own X instead). */
function ArticleBindPopover({
  label,
  partial,
  all,
  allLabel,
  selected,
  articleNames,
  onCommit,
  translate,
}: {
  label: string
  partial: boolean
  all: readonly string[]
  /** The "all" option's label: "Alle Artikel", or "flagged articles" while
   *  the N9 pool fallback narrows `all` to the gate-flagged articles. */
  allLabel: string
  selected: readonly string[]
  articleNames?: Map<string, string>
  onCommit: (next: string[]) => void
  translate: Translate
}) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState("")
  const selectedSet = new Set(selected)
  const everything = selected.length === all.length
  const needle = q.trim().toLowerCase()
  const shown = needle
    ? all.filter(
        (a) =>
          a.toLowerCase().includes(needle) ||
          (articleNames?.get(a) ?? "").toLowerCase().includes(needle),
      )
    : all

  const toggle = (articleNumber: string) => {
    const next = all.filter((a) =>
      a === articleNumber ? !selectedSet.has(a) : selectedSet.has(a),
    )
    if (next.length === 0) return
    onCommit(next)
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) setQ("")
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn("sv2-stab-bind", partial && "sv2-stab-bind-part")}
          title={translate("MC_DECL_V2_COND_BIND_TOOLTIP")}
        >
          {label}
          <ChevronDown className="sv2-ic" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        // Same escape hatch as every in-card popover: without it the
        // content portals outside `.sv2-root` and the scoped tokens never
        // resolve there.
        disablePortal
        align="end"
        className="w-[300px] rounded-none border-0 bg-transparent p-0 shadow-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="sv2-mappop sv2-stab-bindpop"
          style={{ position: "static" }}
        >
          <div className="sv2-mh">
            {translate("MC_DECL_V2_COND_BIND_LABEL")}
          </div>
          {all.length > 8 && (
            <input
              type="text"
              className="sv2-acin w-full"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={translate("MC_DECL_V2_SUBST_BIND_FILTER")}
              aria-label={translate("MC_DECL_V2_SUBST_BIND_FILTER")}
              autoFocus
            />
          )}
          <div className="sv2-maplist" role="listbox" aria-multiselectable>
            <button
              type="button"
              role="option"
              aria-selected={everything}
              className="sv2-acitem sv2-stab-bindall w-full text-left"
              onClick={() => onCommit([...all])}
            >
              <span className="sv2-apo">{allLabel}</span>
              {everything && <Check size={13} />}
            </button>
            {shown.map((a) => (
              <button
                key={a}
                type="button"
                role="option"
                aria-selected={selectedSet.has(a)}
                className="sv2-acitem w-full text-left"
                onClick={() => toggle(a)}
              >
                <span className="sv2-apo">{a}</span>
                {articleNames?.get(a) && (
                  <span className="sv2-an">{articleNames.get(a)}</span>
                )}
                {selectedSet.has(a) && <Check size={13} />}
              </button>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

/** The registry search: one input, the hits under it, a hit is a pick. Used
 *  by "+ Stoff" (adds a substance to the statement) and by the nameless
 *  slot's "Stoff wählen" (names the slot). Never creates a substance: that
 *  stays the master-data screen's own gesture. */
function RegistrySearch({
  exclude,
  onPick,
  label,
  icon,
  autoFocus = false,
  translate,
}: {
  /** Registry keys already on the statement: never offered twice. */
  exclude: ReadonlySet<string>
  onPick: (s: SubstanceOut) => void
  /** The input's accessible name. */
  label: string
  icon?: ReactNode
  autoFocus?: boolean
  translate: Translate
}) {
  const [q, setQ] = useState("")
  const debounced = useDebouncedValue(q, 250).trim()
  const active = debounced.length >= 2
  const {
    data: pages,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useGlobalSubstanceList({
    q: active ? debounced : undefined,
    limit: 8,
    enabled: active,
  })
  const hits = useMemo(
    () =>
      (pages?.pages.flatMap((p) => p.items) ?? []).filter(
        (s) => !exclude.has(registryKey(s)),
      ),
    [pages, exclude],
  )
  const typed = q.trim()

  return (
    <div>
      <div className="sv2-stab-add">
        {icon}
        <input
          type="text"
          className="sv2-acin"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={translate("MC_DECL_V2_COND_SUBSTANCE_PICK_SEARCH")}
          aria-label={label}
          autoFocus={autoFocus}
        />
      </div>
      {typed.length > 0 && (
        <div className="sv2-stab-hits" role="listbox">
          {typed.length < 2 && (
            <p className="sv2-acempty">
              {translate("MC_DECL_V2_SUBST_SEARCH_MIN")}
            </p>
          )}
          {active && !isFetching && !hasNextPage && hits.length === 0 && (
            <p className="sv2-acempty">
              {translate("MC_DECL_V2_SUBST_SEARCH_EMPTY")}
            </p>
          )}
          {hits.map((s) => (
            <button
              key={s.id}
              type="button"
              role="option"
              aria-selected={false}
              className="sv2-stab-hit"
              onClick={() => {
                onPick(s)
                setQ("")
              }}
            >
              <span className="sv2-stab-hitname">
                {substanceDisplayName(s.name, s.casNumber, translate)}
              </span>
              {s.casNumber && (
                <span className="sv2-stab-scas">{s.casNumber}</span>
              )}
            </button>
          ))}
          {active && hasNextPage && (
            <button
              type="button"
              className="sv2-stab-hit"
              disabled={isFetchingNextPage}
              onClick={() => void fetchNextPage()}
            >
              {translate("MC_DECL_LOAD_MORE")}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

/** The nameless slot's trigger: "Choose substance", the registry search in a
 *  popover, the pick rekeyed server-side (`commitSubstance`). While the pick
 *  saves (one request for the whole slot, TUR-1725) the trigger shows the
 *  picked substance's name and CAS with a spinner and "Saving…", disabled so
 *  a second pick cannot start; once it has landed, the name and CAS the way a
 *  named slot shows them until the refetch names the slot; on a refusal
 *  "Choose substance" again with the reason below it (nothing was written).
 *  When the server skipped articles of the slot list (no slot in the record
 *  yet, or gone), a note says for how many it saved and, per kind, which
 *  articles were skipped and what to do (`PickNote`); when it applied the pick
 *  to no article, the same note leads with "Nothing saved". */
function PickSubstanceControl({
  pickState,
  onPick,
  translate,
}: {
  pickState?: SubstancePick
  onPick: (s: SubstanceOut) => void
  translate: Translate
}) {
  const [open, setOpen] = useState(false)
  // Some articles of the slot list had no slot (or are gone): the rest was
  // saved, and the row says so, until the next pick.
  const skippedNote =
    (pickState?.status === "saved" || pickState?.status === "partial") &&
    skippedCount(pickState.skipped) > 0 ? (
      <PickNote
        head={translate("MC_DECL_V2_COND_SUBSTANCE_PICK_PARTIAL", {
          n: pickState.applied,
        })}
        skipped={pickState.skipped}
        translate={translate}
      />
    ) : pickState?.status === "nothing" ? (
      // Nothing applied (every article already had a substance, or no slot):
      // said on the row rather than a silent "Choose substance" again.
      <PickNote
        head={
          pickState.alreadySet > 0
            ? translate("MC_DECL_V2_COND_SUBSTANCE_PICK_NOTHING_SET", {
                n: pickState.alreadySet,
              })
            : translate("MC_DECL_V2_COND_SUBSTANCE_PICK_NOTHING_SAVED")
        }
        skipped={pickState.skipped}
        translate={translate}
        testId="substance-pick-nothing"
      />
    ) : null
  if (pickState?.status === "saved") {
    const { name, casNumber } = pickState.substance
    return (
      <span data-testid="substance-pick-saved">
        {substanceDisplayName(name, casNumber, translate)}
        {casNumber && (
          <span
            className="sv2-stab-scas"
            title={translate("MC_DECL_V2_COND_CAS_LABEL_TITLE")}
          >
            {casNumber}
          </span>
        )}
        {skippedNote}
      </span>
    )
  }
  const pending = pickState?.status === "pending" ? pickState.substance : null
  return (
    <span>
      <Popover open={open && !pending} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="sv2-stab-pick"
            aria-busy={pending !== null}
            disabled={pending !== null}
            data-testid="substance-pick-trigger"
            title={translate("MC_DECL_V2_COND_SUBSTANCE_PICK_TOOLTIP")}
            // Inside the card header, whose own click toggles the card.
            onClick={(e) => e.stopPropagation()}
          >
            {pending ? (
              <>
                <Loader2
                  className="sv2-ic mr-1 inline animate-spin"
                  aria-hidden="true"
                />
                {substanceDisplayName(
                  pending.name,
                  pending.casNumber,
                  translate,
                )}
                {pending.casNumber ? ` ${pending.casNumber}` : ""}
                {` · ${translate("MC_DECL_V2_COND_SUBSTANCE_PICK_SAVING")}`}
              </>
            ) : (
              translate("MC_DECL_V2_COND_SUBSTANCE_PICK_LABEL")
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent
          disablePortal
          align="start"
          className="w-[340px] rounded-none border-0 bg-transparent p-0 shadow-none"
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="sv2-mappop sv2-stab-pickpop"
            style={{ position: "static" }}
          >
            <RegistrySearch
              exclude={new Set()}
              onPick={(s) => {
                onPick(s)
                setOpen(false)
              }}
              label={translate("MC_DECL_V2_COND_SUBSTANCE_PICK_LABEL")}
              autoFocus
              translate={translate}
            />
          </div>
        </PopoverContent>
      </Popover>
      {pickState?.status === "error" && (
        <p className="mt-1 text-[11px] text-red1" role="alert">
          {pickErrorText(pickState, translate)}
        </p>
      )}
      {skippedNote}
    </span>
  )
}

/** How many articles a note names before "+N more" (the server sends up to
 *  ten of each kind). */
const ARTICLES_NAMED = 3

/** " (A-1, A-2, A-3 +5)", or "" when the answer named none (an older
 *  backend). */
function namedArticles(numbers: readonly string[], total: number): string {
  const shown = numbers.slice(0, ARTICLES_NAMED)
  if (shown.length === 0) return ""
  const more = total - shown.length
  return ` (${shown.join(", ")}${more > 0 ? ` +${more}` : ""})`
}

/** The note under a pick that skipped articles: what was (not) saved, then per
 *  skipped kind how many, which ones, and what to do. Kept until the next pick
 *  (TUR-1725): articles without an unnamed entry get one with the recalculation,
 *  then the same pick finishes the slot; articles gone need nothing. */
function PickNote({
  head,
  skipped,
  translate,
  testId,
}: {
  head: string
  skipped: SkippedArticles
  translate: Translate
  testId?: string
}) {
  return (
    <p className="mt-1 text-[11px]" role="status" data-testid={testId}>
      {head}
      {skipped.noSlot > 0 && (
        <>
          {" "}
          {translate("MC_DECL_V2_COND_SUBSTANCE_PICK_SKIPPED_NO_SLOT", {
            m: skipped.noSlot,
            articles: namedArticles(skipped.noSlotArticles, skipped.noSlot),
          })}
        </>
      )}
      {skipped.notFound > 0 && (
        <>
          {" "}
          {translate("MC_DECL_V2_COND_SUBSTANCE_PICK_SKIPPED_GONE", {
            m: skipped.notFound,
            articles: namedArticles(skipped.notFoundArticles, skipped.notFound),
          })}
        </>
      )}
    </p>
  )
}

/** A refused pick's copy: the two refusals the row lock gives (TUR-1725) say
 *  what happened; the two per-article refusals of the slot-wide pick
 *  (ARTICLE_NOT_FOUND, NO_EMPTY_SLOT: the whole slot is refused) name the
 *  article the server named; a list over the route's cap says the cap;
 *  anything else, that the pick was not saved. */
function pickErrorText(
  {
    code,
    articleNumber,
    maxArticles,
  }: Extract<SubstancePick, { status: "error" }>,
  translate: Translate,
): string {
  // More articles than one request takes: the cap, not "try again" (the
  // same list would be refused again).
  if (maxArticles !== null)
    return translate("MC_DECL_V2_COND_SUBSTANCE_PICK_TOO_MANY", {
      max: maxArticles,
    })
  if (code === "ALREADY_APPROVED")
    return translate("MC_DECL_V2_COND_SUBSTANCE_PICK_APPROVED")
  if (code === "REVIEW_BUSY") return translate("MC_API_ERROR_REVIEW_BUSY")
  if (articleNumber && code === "ARTICLE_NOT_FOUND")
    return translate("MC_DECL_V2_COND_SUBSTANCE_PICK_NO_ARTICLE", {
      article: articleNumber,
    })
  if (articleNumber && code === "NO_EMPTY_SLOT")
    return translate("MC_DECL_V2_COND_SUBSTANCE_PICK_NO_SLOT", {
      article: articleNumber,
    })
  return translate("MC_DECL_V2_COND_SUBSTANCE_PICK_FAILED")
}

/** The registry's name for a substance the statement knows only by its bare
 *  key (a reviewer-added one from an earlier session): by id when the key
 *  is one, else the CAS looked up in the global list. Falls back to the
 *  RoHS-10 display name, then the key itself. */
function RegistryName({
  bare,
  translate,
}: {
  bare: string
  translate: Translate
}) {
  const isId = UUID_RE.test(bare)
  const detail = useGlobalSubstanceDetail(isId ? bare : null)
  const list = useGlobalSubstanceList({
    q: isId ? undefined : bare,
    limit: 5,
    enabled: !isId,
  })
  const hit = isId
    ? detail.data
    : list.data?.pages
        .flatMap((p) => p.items)
        .find((s) => normalizeCas(s.casNumber) === bare)
  if (hit)
    return <>{substanceDisplayName(hit.name, hit.casNumber, translate)}</>
  return <>{substanceDisplayName("", bare, translate) || bare}</>
}
